import React from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation, hexToRgba, resolveLinkProps } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';
import SectionIcon from './SectionIcon';

export default function AudienceCardsSection({ block = {}, primaryColor, secondaryColor, enableAnimations }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const items = content.items || [];
    const anim = getAnimation(config, visible);
    const variant = config.variant || 'equal';

    if (items.length === 0) return null;

    const renderBenefits = (item) => {
        const benefits = (item.benefits || '').split('\n').filter(Boolean);
        return (
            <ul className="space-y-2 mb-6 flex-1">
                {benefits.map((b, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm" style={{ color: s.textSub }}>
                        <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill={s.accent} viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span>{b}</span>
                    </li>
                ))}
            </ul>
        );
    };

    const renderEqualCard = (item, idx) => (
        <div
            key={idx}
            className={`flex flex-col p-6 rounded-2xl border transition-all duration-300 ${anim}`}
            style={{
                borderColor: hexToRgba(s.accent, 0.15),
                backgroundColor: hexToRgba(s.accent, 0.03),
                transitionDelay: `${idx * 100}ms`,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = `0 12px 32px ${hexToRgba(s.accent, 0.12)}`; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
        >
            {item.icon && <div className="mb-4"><SectionIcon name={item.icon} color={s.accent} size={36} /></div>}
            <h3 className="text-xl font-bold mb-3" style={{ color: s.text }}>{item.title}</h3>
            {renderBenefits(item)}
            {item.cta_text && (
                <a
                    {...resolveLinkProps(item.cta_link)}
                    className="inline-flex items-center justify-center px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-opacity hover:opacity-90"
                    style={{ backgroundColor: s.accent }}
                >
                    {item.cta_text}
                </a>
            )}
        </div>
    );

    const renderFeaturedCard = (item, idx) => {
        const isFeatured = idx === 0;
        return (
            <div
                key={idx}
                className={`flex flex-col p-6 rounded-2xl border transition-all duration-300 ${isFeatured ? 'md:col-span-2 lg:col-span-1 lg:row-span-2' : ''} ${anim}`}
                style={{
                    borderColor: isFeatured ? s.accent : hexToRgba(s.accent, 0.15),
                    backgroundColor: isFeatured ? hexToRgba(s.accent, 0.08) : hexToRgba(s.accent, 0.03),
                    borderWidth: isFeatured ? 2 : 1,
                    transitionDelay: `${idx * 100}ms`,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = `0 12px 32px ${hexToRgba(s.accent, 0.12)}`; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
            >
                {item.icon && <div className="mb-4"><SectionIcon name={item.icon} color={s.accent} size={isFeatured ? 44 : 36} /></div>}
                <h3 className={`font-bold mb-3 ${isFeatured ? 'text-2xl' : 'text-xl'}`} style={{ color: s.text }}>{item.title}</h3>
                {renderBenefits(item)}
                {item.cta_text && (
                    <a
                        {...resolveLinkProps(item.cta_link)}
                        className="inline-flex items-center justify-center px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-opacity hover:opacity-90"
                        style={{ backgroundColor: s.accent }}
                    >
                        {item.cta_text}
                    </a>
                )}
            </div>
        );
    };

    const renderMinimalCard = (item, idx) => (
        <div
            key={idx}
            className={`flex flex-col py-6 ${anim}`}
            style={{ transitionDelay: `${idx * 100}ms` }}
        >
            {item.icon && <div className="mb-3"><SectionIcon name={item.icon} color={s.accent} size={32} /></div>}
            <h3 className="text-xl font-bold mb-3" style={{ color: s.text }}>{item.title}</h3>
            {renderBenefits(item)}
            {item.cta_text && (
                <a
                    {...resolveLinkProps(item.cta_link)}
                    className="inline-flex items-center text-sm font-semibold transition-opacity hover:opacity-75"
                    style={{ color: s.accent }}
                >
                    {item.cta_text}
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </a>
            )}
        </div>
    );

    const gridClass = variant === 'featured'
        ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
        : variant === 'minimal'
            ? 'grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-2'
            : 'grid grid-cols-1 md:grid-cols-2 gap-6';

    const renderCard = variant === 'featured'
        ? renderFeaturedCard
        : variant === 'minimal'
            ? renderMinimalCard
            : renderEqualCard;

    return (
        <section ref={ref} style={{ backgroundColor: s.bg }}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div className="max-w-6xl mx-auto px-4 py-16 md:py-24">
                <SectionHeader
                    tag={content.tag}
                    title={content.title}
                    subtitle={content.subtitle}
                    s={s}
                    anim={anim}
                />
                <div className={gridClass}>
                    {items.map((item, idx) => renderCard(item, idx))}
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
